import { GtmEvent } from "../events";
import { safeNumber, safeString, stripUndefined, flattenArray } from "../utils";
import {
  mapOrderRowToItem,
  mapOrderRowsToItems,
  serialiseProductItemForGtm,
} from "./productMapper";
import config from "../../../globalConfig";

/**
 * Maps numeric orderTypeId to letter code
 * @param {number|string} orderTypeId - The order type ID (1 = Reorder, 2 = Swap)
 * @returns {string|undefined} "R" for Reorder, "S" for Swap, or undefined
 */
const mapOrderTypeIdToLetter = (orderTypeId) => {
  const numericId = safeNumber(orderTypeId);
  if (numericId === config.ORDER_TYPE_REORDER) return "R";
  if (numericId === config.ORDER_TYPE_SWAP) return "S";
  return undefined;
};

const sumRowQuantities = (rows) =>
  (rows || []).reduce((total, row) => {
    const quantity = safeNumber(row?.quantity || row?.currentQuantity);
    return quantity ? total + quantity : total;
  }, 0);

const serialiseCommerceItems = (items = []) =>
  items
    .map((item) =>
      serialiseProductItemForGtm(item, {
        includeName: true,
        includePricing: true,
      }),
    )
    .filter(Boolean);

const calculateItemsValue = (items = []) =>
  items.reduce((total, item) => {
    const price = safeNumber(item?.price);
    const quantity = safeNumber(item?.quantity);
    if (price === undefined || quantity === undefined) {
      return total;
    }
    return total + price * quantity;
  }, 0);

const lookupSeasonLabel = (configuration, order, language) => {
  if (!configuration?.length || !order) return undefined;
  const allEntries = configuration.flatMap((c) => c.configuration || []);
  const entry = allEntries.find((c) => c.seasonId == order?.seasonId);
  if (!entry) return undefined;
  const desc = entry.seasonDescription?.[language];
  return desc ? `${entry.season} - ${desc}` : entry.season || undefined;
};

const lookupLineLabel = (configuration, order, language) => {
  if (!configuration?.length || !order) return undefined;
  const allEntries = configuration.flatMap((c) => c.configuration || []);
  const lineId = order?.customData?.lineId ?? order?.lineId;
  const entry = allEntries.find(
    (c) => c.lineID == lineId && c.seasonId == order?.seasonId,
  );
  if (!entry) return undefined;
  const desc = entry.lineDescription?.[language];
  return desc !== undefined
    ? `${entry.line} - ${desc}`
    : entry.line || undefined;
};

const buildOrderCustomData = ({ order, customer, configuration, language }) =>
  stripUndefined({
    Cd_Company: safeString(order?.companyCode || customer?.companyCode),
    Cd_Destinazione: safeString(
      order?.destinationCode || customer?.destinationCode,
    ),
    Cd_Stagione: safeString(
      lookupSeasonLabel(configuration, order, language) ||
        order?.seasonCode ||
        order?.seasonId,
    ),
    Cd_Linea: safeString(
      lookupLineLabel(configuration, order, language) ||
        order?.customData?.lineId ||
        order?.lineId,
    ),
    Cd_Listino: safeString(
      order?.priceContractCode || customer?.priceContractId,
    ),
    Cd_Agente: safeString(
      order?.agentId || order?.agent1Id || customer?.agentId,
    ),
    Cd_TipoOrdine: mapOrderTypeIdToLetter(order?.orderTypeId),
    NumeroOrdine_iShowRoom: safeString(order?.orderNumber || order?.id),
    Cd_Area_Commerciale: safeString(
      order?.customData?.areaCode || customer?.areaCode,
    ),
    Cd_MagazzinoLogico: safeString(
      order?.warehouseCode || customer?.warehouseCode,
    ),
    Cd_Door: Array.isArray(order?.customData?.doors)
      ? order.customData.doors.map((door) => safeString(door))
      : undefined,
    Cd_Collezione: safeString(
      order?.collectionCode || order?.customData?.collectionId,
    ),
  });

export const buildCartEventPayload = ({
  event,
  rows,
  row,
  quantity,
  catalogueState,
  userContext,
  order,
  customer,
  language,
}) => {
  const rawItems = rows
    ? mapOrderRowsToItems({
        rows,
        catalogueState,
        language,
      })
    : flattenArray([
        mapOrderRowToItem({
          row,
          quantity,
          catalogueState,
          language,
        }),
      ]);

  const items = serialiseCommerceItems(rawItems);

  if (!items.length) {
    return undefined;
  }

  const ecommerceValue = calculateItemsValue(items);

  return stripUndefined({
    event,
    account: userContext?.account,
    role: userContext?.role,
    ecommerce: {
      value: ecommerceValue,
      items,
    },
  });
};

export const buildBeginCheckoutEvent = ({
  order,
  rows,
  catalogueState,
  customer,
  userContext,
  language,
}) => {
  const rawItems = mapOrderRowsToItems({
    rows,
    catalogueState,
    language,
  });

  const items = serialiseCommerceItems(rawItems);

  if (!items.length) {
    return undefined;
  }

  const ecommerceValue = calculateItemsValue(items);

  return stripUndefined({
    event: GtmEvent.BEGIN_CHECKOUT,
    account: userContext?.account,
    role: userContext?.role,
    checkout_step: 1,
    ecommerce: {
      value: ecommerceValue,
      items,
    },
    orderId: safeString(order?.id),
    orderNumber: safeString(order?.orderNumber),
    orderTypeId: mapOrderTypeIdToLetter(order?.orderTypeId),
    seasonId: safeString(order?.seasonId || order?.seasonCode),
    lineId: safeString(order?.customData?.lineId),
  });
};

export const buildPurchaseEvent = ({
  order,
  rows,
  catalogueState,
  customer,
  userContext,
  language,
  configuration,
}) => {
  const rawItems = mapOrderRowsToItems({
    rows,
    catalogueState,
    language,
  });

  const items = serialiseCommerceItems(rawItems);

  console.log("[GTM DEBUG purchase] rawItems:", rawItems);
  console.log("[GTM DEBUG purchase] items after serialise:", items);
  console.log(
    "[GTM DEBUG purchase] catalogueState passed in:",
    Object.keys(catalogueState || {}),
  );

  if (!items.length) {
    console.warn(
      "[GTM DEBUG purchase] buildPurchaseEvent BLOCKED - items empty. rows were:",
      rows,
    );
    return undefined;
  }

  const value = calculateItemsValue(items);

  return stripUndefined({
    event: GtmEvent.PURCHASE,
    account: userContext?.account,
    role: userContext?.role,
    transaction_id: safeString(order?.orderNumber || order?.id),
    value,
    ecommerce: stripUndefined({
      transaction_id: safeString(order?.orderNumber || order?.id),
      value,
      items,
      tax: safeNumber(order?.taxAmount),
      shipping: safeNumber(order?.shippingAmount),
      affiliation: safeString(customer?.name),
    }),
    totalQuantity: sumRowQuantities(rows),
    ...buildOrderCustomData({ order, customer, configuration, language }),
  });
};

export const buildOrderErrorEvent = ({ order, userContext, error }) =>
  stripUndefined({
    event: GtmEvent.ORDER_SUBMISSION_ERROR,
    account: userContext?.account,
    role: userContext?.role,
    orderId: safeString(order?.id),
    orderNumber: safeString(order?.orderNumber),
    errorCode: safeString(error?.code || error?.errorCode || error?.status),
    errorMessage: safeString(error?.message || error?.body?.message),
  });
